import React from 'react';
import { Row, Col, Card, Statistic, Spin, Typography } from 'antd';
import {
  DollarOutlined,
  CalendarOutlined,
  RiseOutlined,
  TrophyOutlined,
  UserOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const { Title, Text } = Typography;

const money = (v) => Number(v || 0);

const PeriodCard = ({ title, sales, profit, count, icon, color }) => (
  <Card size="small" styles={{ body: { padding: '16px 18px' } }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 6 }}>{title}</div>
        <Statistic
          value={money(sales)}
          prefix="¥"
          precision={2}
          valueStyle={{ color, fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}
        />
        <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 12 }}>
          利润 ¥{money(profit).toFixed(2)} · {count || 0} 笔
        </div>
      </div>
      <div style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: `${color}14`,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0
      }}>
        {icon}
      </div>
    </div>
  </Card>
);

const Dashboard = ({ stats, loading, isMobile }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!stats) {
    return <div>暂无数据</div>;
  }

  const period = stats.periodSales || {};
  const today = period.today || {};
  const yesterday = period.yesterday || {};
  const month = period.month || {};
  const year = period.year || {};

  const monthlyData = (stats.monthlySales || []).map((item) => ({
    month: item.month,
    sales: money(item.sales),
    profit: money(item.profit),
    count: item.count || 0
  }));

  // 最近 6 个月（不足则全量）
  const chartData = monthlyData.slice(-6);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0 }}>仪表盘</Title>
        <Text type="secondary">销售经营一览（不含采购）</Text>
      </div>

      {/* 库存快照：置顶并加大 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={6}>
          <Card styles={{ body: { padding: '20px 22px' } }}>
            <Statistic
              title={<span style={{ fontSize: 15 }}>总账号</span>}
              value={stats.totalAccounts || 0}
              prefix={<UserOutlined style={{ fontSize: 22 }} />}
              valueStyle={{ fontSize: 32, fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card styles={{ body: { padding: '20px 22px' } }}>
            <Statistic
              title={<span style={{ fontSize: 15 }}>待售</span>}
              value={stats.pendingAccounts || 0}
              prefix={<ShoppingCartOutlined style={{ fontSize: 22 }} />}
              valueStyle={{ color: '#fa8c16', fontSize: 32, fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card styles={{ body: { padding: '20px 22px' } }}>
            <Statistic
              title={<span style={{ fontSize: 15 }}>已售</span>}
              value={stats.soldAccounts || 0}
              prefix={<TrophyOutlined style={{ fontSize: 22 }} />}
              valueStyle={{ color: '#1677ff', fontSize: 32, fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card styles={{ body: { padding: '20px 22px' } }}>
            <Statistic
              title={<span style={{ fontSize: 15 }}>起号</span>}
              value={stats.bannedAccounts || 0}
              prefix={<UserOutlined style={{ fontSize: 22 }} />}
              valueStyle={{ color: '#52c41a', fontSize: 32, fontWeight: 600 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 核心：今日 / 昨日 / 本月 / 本年 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <PeriodCard
            title="今日销售"
            sales={today.sales}
            profit={today.profit}
            count={today.count}
            icon={<DollarOutlined />}
            color="#1677ff"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <PeriodCard
            title="昨日销售"
            sales={yesterday.sales}
            profit={yesterday.profit}
            count={yesterday.count}
            icon={<CalendarOutlined />}
            color="#eb2f96"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <PeriodCard
            title="本月销售"
            sales={month.sales}
            profit={month.profit}
            count={month.count}
            icon={<RiseOutlined />}
            color="#fa8c16"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <PeriodCard
            title="今年销售"
            sales={year.sales}
            profit={year.profit}
            count={year.count}
            icon={<TrophyOutlined />}
            color="#52c41a"
          />
        </Col>
      </Row>

      {/* 销售统计：全宽，不再并列采购图 */}
      <Card
        title="销售统计"
        styles={{ body: { paddingTop: 8 } }}
      >
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 260 : 340}>
            <AreaChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1677ff" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#1677ff" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === '销售额' || name === '利润') return [`¥${Number(value).toFixed(2)}`, name];
                  return [value, name];
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="sales"
                name="销售额"
                stroke="#1677ff"
                fill="url(#salesFill)"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="profit"
                name="利润"
                stroke="#52c41a"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', padding: '64px 16px', color: '#999' }}>
            暂无销售记录，完成出单后将显示趋势
          </div>
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
